import { useId, useMemo, useState } from 'react';
import type { BillDTO, BillPaymentDTO } from '@six-pockets/shared';
import { ChevronDownIcon } from '@/components/icons';
import { Avatar, Button, KeyValue, Modal, Panel, Section, StatusPill } from '@/components/ui';
import { BillBookingsTable, type BillBookingRow } from '@/features/billing/components/BillBookingsTable';
import { itemsSummary } from '@/features/condiments/utils';
import { formatDateTime, formatINR, formatPhone, formatShortDuration } from '@/utils/format';
import { cx } from '@/utils/cx';
import { BILL_STATUS, namesOnBill, paymentKindLabel, paymentParts } from '../utils';
import styles from '@/styles/components/BillDetailsModal/style.scss';

/**
 * One payment as the customer made it: when, and the whole amount (₹725). When it was shared across bills, the
 * line under it says what went to this bill ("₹108 to this bill"). The chevron opens how it was paid (UPI · ₹725)
 * and, for a shared payment, this bill's share ("₹108 of ₹725"); the other bills are not listed (owner's call).
 */
function PaymentRow({ payment: p, billCreatedAt }: { payment: BillPaymentDTO; billCreatedAt: string }) {
  const [open, setOpen] = useState(false);
  const partsId = useId();
  const shared = p.otherBills.length > 0;

  return (
    <li className={cx(styles.payment, open && styles.paymentOpen)}>
      <button
        type="button"
        className={styles.paymentHead}
        aria-expanded={open}
        aria-controls={partsId}
        aria-label={`Payment on ${formatDateTime(p.paidAt)}, ${formatINR(p.total)}${shared ? `, ${formatINR(p.amount)} to this bill` : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={styles.paymentWhen}>
          <span className={styles.paidAt}>{formatDateTime(p.paidAt)}</span>
          <span className={styles.paymentKind}>{paymentKindLabel(p.kind, p.paidAt, billCreatedAt)}</span>
        </span>
        <span className={styles.paymentMoney}>
          <span className={styles.paidAmount}>{formatINR(p.total)}</span>
          {shared && <span className={styles.share}>{formatINR(p.amount)} to this bill</span>}
          {p.discount > 0 && <span className={styles.discount}>{formatINR(p.discount)} discount</span>}
        </span>
        <ChevronDownIcon size={16} className={styles.chevron} />
      </button>
      {open && (
        <div id={partsId} className={styles.paymentDetails}>
          <ul className={styles.parts}>
            {paymentParts({ method: p.method, amount: p.total, upi: p.totalUpi, cash: p.totalCash }).map((part) => (
              <li key={part.label} className={styles.part}>
                <span className={styles.partLabel}>{part.label}</span>
                <span className={styles.partAmount}>{formatINR(part.amount)}</span>
              </li>
            ))}
          </ul>
          {/* Only this bill's share, not the other bills the payment went to. */}
          {shared && (
            <ul className={styles.parts}>
              <li className={cx(styles.part, styles.thisBill)}>
                <span className={styles.partLabel}>This bill</span>
                <span className={styles.partAmount}>
                  {formatINR(p.amount)} of {formatINR(p.total)}
                </span>
              </li>
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

interface BillDetailsModalProps {
  bill: BillDTO;
  onClose: () => void;
}

/**
 * Everything on a saved bill: who it was billed to, the bookings and condiment tabs (as snapshotted), totals with
 * the discount, and every payment (at billing and each balance collected), with what is still due. Read only.
 */
export function BillDetailsModal({ bill, onClose }: BillDetailsModalProps) {
  const status = BILL_STATUS[bill.status];
  const bookings = useMemo<BillBookingRow[]>(
    () => bill.bookings.map((b) => ({ ...b, id: b.bookingId, accumulatedMs: b.durationMs })),
    [bill.bookings],
  );
  const pausedMs = bill.bookings.reduce((a, b) => a + b.pausedMs, 0);
  // On a split booking's line these are this bill's part of the pause.
  const pauseAmount = bill.bookings.reduce((a, b) => a + b.pauseAmount, 0);
  const hasBookings = bill.bookings.length > 0;
  const names = namesOnBill(bill);

  return (
    <Modal
      open
      onClose={onClose}
      title={`Bill #${bill.billNumber}`}
      subtitle={formatDateTime(bill.createdAt)}
      width={680}
      footer={
        <Button variant="secondary" size="lg" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className={styles.top}>
        <Section title="Billed to" className={styles.billedTo}>
          {bill.customer ? (
            <div className={styles.customer}>
              <Avatar name={bill.customer.name} tone="green" />
              <span className={styles.customerText}>
                <span className={styles.customerName}>{bill.customer.name}</span>
                <span className={styles.customerPhone}>{formatPhone(bill.customer.phone)}</span>
              </span>
            </div>
          ) : (
            <p className={styles.noCustomer}>No customer{names && <span> · {names}</span>}</p>
          )}
        </Section>
        <StatusPill tone={status.tone} label={status.label} />
      </div>

      {hasBookings && <BillBookingsTable bookings={bookings} showTable />}

      {bill.condimentSales.length > 0 && (
        <Section title="Condiments">
          <ul className={styles.tabs}>
            {bill.condimentSales.map((s) => (
              <li key={s.saleId} className={styles.tab}>
                <span className={styles.tabName}>{s.tabName}</span>
                <span className={styles.tabItems}>{itemsSummary(s.items)}</span>
                <span className={styles.tabTotal}>{formatINR(s.total)}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Panel>
        {hasBookings && <KeyValue label="Table time" value={formatINR(bill.bookingsTotal)} />}
        {hasBookings && pausedMs > 0 && (
          <div className={styles.pauseRow}>
            <span>Pause · {formatShortDuration(pausedMs)} (in table time)</span>
            <span className={styles.pauseAmount}>{formatINR(pauseAmount)}</span>
          </div>
        )}
        <KeyValue label="Condiments" value={formatINR(bill.condimentsTotal)} />
        <KeyValue label="Subtotal" value={formatINR(bill.subtotal)} />
        {bill.discount > 0 && <KeyValue label="Discount" value={`− ${formatINR(bill.discount)}`} />}
        <KeyValue label="Total" value={formatINR(bill.total)} emphasis />
      </Panel>

      <Section title="Payments">
        {bill.payments.length === 0 ? (
          bill.waivers.length === 0 && (
            <p className={styles.noPayments}>{bill.status === 'waived' ? 'Waived: nothing to pay' : 'Nothing paid yet'}</p>
          )
        ) : (
          <ul className={styles.payments}>
            {bill.payments.map((p) => (
              <PaymentRow key={p.paymentId} payment={p} billCreatedAt={bill.createdAt} />
            ))}
          </ul>
        )}
        {/* Balance let off at Pay now in place of money; recorded as an expense with this reason. */}
        {bill.waivers.length > 0 && (
          <ul className={styles.waivers}>
            {bill.waivers.map((w, i) => (
              <li key={i} className={styles.waiverRow}>
                <span className={styles.waiverMain}>
                  <span className={styles.waiverWhen}>{formatDateTime(w.waivedAt)}</span>
                  <span className={styles.waiverReason}>Waived · {w.reason}</span>
                </span>
                <span className={styles.waiverAmount}>{formatINR(w.amount)}</span>
              </li>
            ))}
          </ul>
        )}
        {(bill.payments.length > 1 || bill.balanceDue > 0 || bill.waived > 0) && (
          <div className={styles.paidSummary}>
            <KeyValue label="Paid" value={formatINR(bill.paidAmount)} />
            {bill.waived > 0 && <KeyValue label="Waived" value={formatINR(bill.waived)} />}
            {bill.balanceDue > 0 && (
              <div className={styles.dueRow}>
                <span>Balance due</span>
                <span className={styles.dueValue}>{formatINR(bill.balanceDue)}</span>
              </div>
            )}
          </div>
        )}
      </Section>
    </Modal>
  );
}
